import { useFormik } from "formik";
import { LngLat } from "mapbox-gl";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useLeaseCompStore, useMapMarkerStore, usePipedriveStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useLeaseCompCreate from "../../hooks/useLeaseCompCreate";
import useLeaseCompValidationSchema from "../../hooks/useLeaseCompValidationSchema";
import useNavigateToLeaseComp from "../../hooks/useNavigateToLeaseComp";
import useProperty from "../../hooks/useProperty";
import DetailsType from "../../interfaces/DetailsType";
import { GeoCoordinate } from "../../interfaces/GeoCoordinate";
import { ILeaseCompState, LeaseCompState } from "../../interfaces/ILeaseComp";
import { ClosableOrder } from "../../store/ClosableStore";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import DetailsPanel from "../DetailsPanel";
import { CreateControls } from "../EditControls";
import LeaseCompIcon from "../icons/LeaseCompIcon";
import OnMapSpinner from "../spinners/OnMapSpinner";
import LeaseCompFields from "./LeaseCompFields";

function LeaseCompCreate() {
    const { longitude, latitude } = useParams();
    const navigate = useNavigate();
    const navigateToLeaseComp = useNavigateToLeaseComp();
    const leaseCompStore = useLeaseCompStore();
    const mapMarkerStore = useMapMarkerStore();
    const pipedriveStore = usePipedriveStore();
    const coordinates = useMemo(() => new LngLat(Number(longitude), Number(latitude)), [longitude, latitude]);
    const validationSchema = useLeaseCompValidationSchema();
    const { property, isLoading, fetchProperty } = useProperty(coordinates);
    const createLeaseComp = useLeaseCompCreate();
    const initialState: ILeaseCompState | null = useMemo(() => computed(() => property ? {
        ...LeaseCompState.empty(),
        address: property.address,
        regridId: property.regridId,
        buildingSize: property.buildingSize,
        parcelSizeAcres: property.landSize,
    } : null), [property]).get();

    const formik = useFormik<ILeaseCompState>({
        initialValues: LeaseCompState.empty(),
        validationSchema: toFormikValidationSchema(validationSchema),
        onSubmit: async (values) => {
            const createdLeaseCompId = await createLeaseComp(values, property!.regridId);
            navigateToLeaseComp(createdLeaseCompId, true);
        },
    });

    useEffect(() => {
        if (initialState)
            formik.resetForm({ values: initialState });
    }, [initialState]);

    useEffect(() => {
        fetchProperty();
        mapMarkerStore.draw(new GeoCoordinate(coordinates.lat, coordinates.lng));
        return () => mapMarkerStore.clear();
    }, [coordinates]);

    const close = () => navigate("/");
    const cancel = () => navigate(-1);
    useClosable(ClosableOrder.Medium, cancel);

    if (isLoading)
        return <OnMapSpinner />;

    if (!property)
        return null;

    return (
        <DetailsPanel type={DetailsType.LeaseCompCreate} onClose={close}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    <LeaseCompIcon color="#25479A" />
                    <div className={detailsStyles.title}>
                        <span className={commonStyles.primary}>Add New Lease Comp</span>
                    </div>
                </div>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={detailsStyles.body}>
                <LeaseCompFields formik={formik} />
            </div>
            <div className={commonStyles.separator}></div>
            <div className={commonStyles.controls}>
                <div className={commonStyles.controlGroup}>
                    <CreateControls
                        isStateChanged={formik.dirty || !mapMarkerStore.isMatch(new GeoCoordinate(coordinates.lat, coordinates.lng))}
                        isCreateLoading={leaseCompStore.isLeaseCompsLoading.creating! || pipedriveStore.isOrganizationsLoading.creating!}
                        onCreate={formik.handleSubmit}
                        onCancel={cancel}
                    />
                </div>
                <div className={commonStyles.controlGroup}></div>
            </div>
        </DetailsPanel>
    );
}

export default observer(LeaseCompCreate);